if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.openTimeout = null;
      this.focusFrame = null;
      this.setHeaderCartIconAccessibility();
    }

    init() {
      if (!this.isInitialized) {
        window.loadTemplateContent(this);

        requestAnimationFrame(() => {
          // Bind various things
          this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      const sections = this.getSectionsToRender().map((section) => section.id);
      fetch(cartSectionUrl(`?sections=${sections}`))
        .then((response) => response.json())
        .then((response) => {
          this.renderContents({
            sections: {
              ...response
            }
          }, true);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
      cssLinks.forEach(link => {
        const href = link.href;
        const alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .some(existingLink => existingLink !== link && existingLink.href === href);

        if (alreadyLoaded) link.remove();
        else document.head.appendChild(link);
      });
    }

    hydrateDeferredRecommendations() {
      this.querySelectorAll('template[data-cart-drawer-recommendations]').forEach(template => {
        template.replaceWith(template.content.cloneNode(true));
      });
      this.querySelectorAll('brincatoys-cart-addons').forEach(addons => addons.retry?.());
      this.moveCssLinks();
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      this.init();
      this.hydrateDeferredRecommendations();

      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.setUtilityAccordionsAccessibility();
      this.removeDeleteTabIndex();

      // Keep deferred activation for the slide-in animation, but do not depend
      // on transitionend: reduced motion can remove the transition entirely.
      clearTimeout(this.openTimeout);
      cancelAnimationFrame(this.focusFrame);
      this.openTimeout = setTimeout(() => {
        this.openTimeout = null;
        this.classList.add('animate', 'active');

        // The visibility transition can remain hidden for more than one frame.
        // Wait for the actual visible control, including with reduced motion.
        const focusWhenVisible = () => {
          this.focusFrame = null;
          if (!this.classList.contains('active')) return;

          const dialog = this.querySelector('.drawer__inner');
          const emptyPanel = this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty');
          const containerToTrapFocusOn = emptyPanel || dialog;
          const focusElement = emptyPanel ? emptyPanel.querySelector('.drawer__close') : dialog;
          if (getComputedStyle(focusElement).visibility !== 'visible') {
            this.focusFrame = requestAnimationFrame(focusWhenVisible);
            return;
          }
          if (!containerToTrapFocusOn.contains(document.activeElement)) focusElement.focus({preventScroll: true});
          // Install the keyboard loop without a second delayed focus that
          // could interrupt a customer who has already started using a field.
          trapFocus(containerToTrapFocusOn, null);
        };
        this.focusFrame = requestAnimationFrame(focusWhenVisible);

        publish(PUB_SUB_EVENTS.cartOpen);
      });

      document.body.classList.add('overflow-hidden');
    }

    close() {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      publish(PUB_SUB_EVENTS.cartClose);
    }

    /**
     * Ensure all remove buttons can be tabbed to
     */
    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => {
        button.removeAttribute('tabindex')
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    setUtilityAccordionsAccessibility() {
      this.querySelectorAll('.bt-cart-drawer__utilities summary').forEach((summary) => {
        if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
      });
    }

    renderContents(parsedState, preventOpen = false) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      setTimeout(() => {
        window.loadTemplateContent(this);
        if (this.classList.contains('active')) this.hydrateDeferredRecommendations();
        this.setUtilityAccordionsAccessibility();
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      });

      this.removeDeleteTabIndex();

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) {
        setTimeout(() => {
          this.open();
        });
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const deferredOpeningTag = /<template\s+class=["']deferred["']\s*>/i;
      if (deferredOpeningTag.test(html)) {
        html = html.replace(deferredOpeningTag, '');
        const deferredClosingTag = html.lastIndexOf('</template>');
        if (deferredClosingTag !== -1) {
          html = `${html.slice(0, deferredClosingTag)}${html.slice(deferredClosingTag + '</template>'.length)}`;
        }
      }
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
